/** @jsxImportSource @emotion/react */
import React from 'react'
import { css } from '@emotion/react'
import { useTranslation } from 'react-i18next'
import {
    Chip,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
} from '@mui/material'
import { CompanyInvoicingOutcome, InvoicingRun } from '../types/invoicing-run.type'

const styles = {
    summary: css`
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
        gap: 12px 24px;
        margin-bottom: 16px;
    `,
    summaryItem: css`
        display: flex;
        flex-direction: column;
    `,
    label: css`
        font-size: 12px;
        color: rgba(0, 0, 0, 0.6);
    `,
    value: css`
        font-size: 16px;
        font-weight: 600;
    `,
    valueAlert: css`
        font-size: 16px;
        font-weight: 600;
        color: #d32f2f;
    `,
    meta: css`
        font-size: 12px;
        color: rgba(0, 0, 0, 0.7);
        margin-bottom: 16px;
        line-height: 1.6;
    `,
    tableWrapper: css`
        max-height: 360px;
        overflow: auto;
    `,
}

const monthName = (m: number) =>
    ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'][m - 1]

const formatRange = (run: InvoicingRun) => {
    const from = `${monthName(run.fromMonth)} ${run.fromYear}`
    const to = `${monthName(run.toMonth)} ${run.toYear}`
    return from === to ? from : `${from} → ${to}`
}

const outcomeChipColor = (outcome: CompanyInvoicingOutcome): 'success' | 'default' | 'warning' | 'error' => {
    switch (outcome) {
        case 'Invoiced':
            return 'success'
        case 'NothingToInvoice':
            return 'default'
        case 'Orphaned':
            return 'warning'
        case 'Failed':
            return 'error'
    }
}

type Props = {
    run: InvoicingRun
}

export const InvoicingRunReport: React.FC<Props> = ({ run }) => {
    const { t } = useTranslation()
    const orphanedTotal = run.totalOrdersOrphaned + run.companiesFailed
    return (
        <div>
            <div css={styles.meta}>
                <div>
                    <strong>{t('TEXT_RUN_BY', 'Run by')}:</strong> {run.runByUserName}
                </div>
                <div>
                    <strong>{t('TEXT_RUN_AT', 'Run at')}:</strong> {new Date(run.runAt).toLocaleString()}
                </div>
                <div>
                    <strong>{t('TEXT_RANGE', 'Range')}:</strong> {formatRange(run)}
                </div>
                <div>
                    <strong>{t('TEXT_STATUS', 'Status')}:</strong>{' '}
                    <Chip
                        size="small"
                        label={run.status}
                        color={run.status === 'Completed' ? 'success' : 'warning'}
                    />
                </div>
            </div>

            <div css={styles.summary}>
                <div css={styles.summaryItem}>
                    <span css={styles.label}>{t('TEXT_COMPANIES_PROCESSED', 'Companies processed')}</span>
                    <span css={styles.value}>{run.totalCompanies}</span>
                </div>
                <div css={styles.summaryItem}>
                    <span css={styles.label}>{t('TEXT_COMPANIES_INVOICED', 'Invoiced')}</span>
                    <span css={styles.value}>{run.companiesInvoiced}</span>
                </div>
                <div css={styles.summaryItem}>
                    <span css={styles.label}>{t('TEXT_COMPANIES_NOTHING', 'Nothing to invoice')}</span>
                    <span css={styles.value}>{run.companiesNothingToInvoice}</span>
                </div>
                <div css={styles.summaryItem}>
                    <span css={styles.label}>{t('TEXT_COMPANIES_ORPHANED', 'Orphaned')}</span>
                    <span css={run.companiesOrphaned > 0 ? styles.valueAlert : styles.value}>
                        {run.companiesOrphaned}
                    </span>
                </div>
                <div css={styles.summaryItem}>
                    <span css={styles.label}>{t('TEXT_COMPANIES_FAILED', 'Failed')}</span>
                    <span css={run.companiesFailed > 0 ? styles.valueAlert : styles.value}>
                        {run.companiesFailed}
                    </span>
                </div>
                <div css={styles.summaryItem}>
                    <span css={styles.label}>{t('TEXT_TOTAL_ORDERS_INVOICED', 'Orders invoiced')}</span>
                    <span css={styles.value}>{run.totalOrdersInvoiced}</span>
                </div>
                <div css={styles.summaryItem}>
                    <span css={styles.label}>{t('TEXT_TOTAL_ORDERS_ORPHANED', 'Orders left unbilled')}</span>
                    <span css={orphanedTotal > 0 ? styles.valueAlert : styles.value}>
                        {run.totalOrdersOrphaned}
                    </span>
                </div>
            </div>

            <TableContainer component={Paper} elevation={0} css={styles.tableWrapper}>
                <Table size="small" stickyHeader>
                    <TableHead>
                        <TableRow>
                            <TableCell>{t('TEXT_REALTOR_COMPANY', 'Meglerkontor')}</TableCell>
                            <TableCell align="right">{t('TEXT_ORDERS_CONSIDERED', 'Considered')}</TableCell>
                            <TableCell align="right">{t('TEXT_ORDERS_INVOICED', 'Invoiced')}</TableCell>
                            <TableCell align="right">{t('TEXT_ORDERS_ORPHANED', 'Orphaned')}</TableCell>
                            <TableCell>{t('TEXT_STATUS', 'Status')}</TableCell>
                            <TableCell>{t('TEXT_NOTES', 'Notes')}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {run.companies.map((c) => (
                            <TableRow key={c.companyId} hover>
                                <TableCell>{c.companyName || c.companyId}</TableCell>
                                <TableCell align="right">{c.ordersConsidered}</TableCell>
                                <TableCell align="right">{c.ordersInvoiced}</TableCell>
                                <TableCell align="right">{c.orphanedOrderIds.length}</TableCell>
                                <TableCell>
                                    <Chip size="small" label={c.outcome} color={outcomeChipColor(c.outcome)} />
                                </TableCell>
                                <TableCell>
                                    {c.invoiceId && (
                                        <span>
                                            {t('TEXT_INVOICE_ID', 'Invoice ID')}: {c.invoiceId}
                                        </span>
                                    )}
                                    {c.errorMessage && (
                                        <span style={{ color: '#d32f2f' }}>{c.errorMessage}</span>
                                    )}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </div>
    )
}
